import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Panel } from "./Panel";
import { formatDateWithOptionsBs } from "@/lib/format";
import { formatTime } from "../helpers";
import { SESSION_STATUS_CONFIG } from "@/lib/status-configs";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { ArrowDown, ArrowUp, Note, Star } from "@phosphor-icons/react";
import type { TherapySession } from "@/types";

const PER_PAGE = 12;

type Filter = "all" | "scheduled" | "completed" | "cancelled" | "no_show";

const FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "Sve" },
  { value: "scheduled", label: "Zakazane" },
  { value: "completed", label: "Završene" },
  { value: "cancelled", label: "Otkazane" },
  { value: "no_show", label: "Nedolasci" },
];

/**
 * `cancelledBy` holds either a sentinel role written by the automated paths or
 * the user id of whoever pressed cancel, so it is resolved against the two ids
 * we know before falling back to a neutral label.
 */
function describeCanceller(
  session: TherapySession,
  therapistUserId: string
): string | null {
  const by = session.cancelledBy;
  if (!by) return null;
  if (by === "therapist" || by === therapistUserId) return "Terapeut";
  if (by === "client" || by === session.clientId) return "Klijent";
  if (by === "admin") return "Admin";
  if (by === "system") return "Sistem";
  return "Klijent";
}

function SessionRow({
  session,
  therapistUserId,
}: {
  session: TherapySession;
  therapistUserId: string;
}) {
  const statusCfg = SESSION_STATUS_CONFIG[session.status];
  const StatusIcon = statusCfg.icon;
  const date = new Date(session.scheduledAt);
  const canceller = describeCanceller(session, therapistUserId);
  const lowRating = typeof session.rating === "number" && session.rating <= 2;

  return (
    <tr className="border-t border-border/50 transition-colors hover:bg-muted/40">
      <td className="whitespace-nowrap px-3 py-2 align-top">
        <span className="block text-sm tabular-nums text-foreground">
          {formatDateWithOptionsBs(date, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            timeZone: "Europe/Sarajevo",
          })}
        </span>
        <span className="block text-xs tabular-nums text-muted-foreground">
          {formatTime(session.scheduledAt)}
        </span>
      </td>

      <td className="max-w-[14rem] px-3 py-2 align-top">
        <span className="block truncate text-sm text-foreground">
          {session.clientName}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {session.clientEmail}
        </span>
      </td>

      <td className="max-w-[13rem] px-3 py-2 align-top">
        <span className="block truncate text-sm text-foreground">
          {session.serviceName || "Individualna terapija"}
        </span>
        <span className="block text-xs tabular-nums text-muted-foreground">
          {session.durationMinutes} min
          {session.modality === "in_person" ? " · uživo" : " · online"}
        </span>
      </td>

      <td className="whitespace-nowrap px-3 py-2 align-top">
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
            statusCfg.bg,
            statusCfg.color
          )}
        >
          <StatusIcon className="size-3" />
          {statusCfg.label}
        </span>
      </td>

      <td className="whitespace-nowrap px-3 py-2 align-top">
        {typeof session.rating === "number" && session.rating > 0 ? (
          <span
            className={cn(
              "inline-flex items-center gap-1 text-sm tabular-nums",
              lowRating ? "font-medium text-rose-600" : "text-foreground"
            )}
          >
            <Star
              weight="fill"
              className={cn("size-3.5", lowRating ? "text-rose-500" : "text-amber-400")}
            />
            {session.rating}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground/40">–</span>
        )}
      </td>

      <td className="whitespace-nowrap px-3 py-2 align-top">
        <span className="inline-flex items-center gap-1.5">
          {canceller ? (
            session.cancelReason ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="rounded text-xs text-foreground underline decoration-dotted underline-offset-2"
                  >
                    {canceller}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{session.cancelReason}</TooltipContent>
              </Tooltip>
            ) : (
              <span className="text-xs text-muted-foreground">{canceller}</span>
            )
          ) : (
            <span className="text-sm text-muted-foreground/40">–</span>
          )}

          {session.notes && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="rounded text-muted-foreground/60 transition-colors hover:text-foreground"
                  aria-label="Bilješka sa sesije"
                >
                  <Note className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{session.notes}</TooltipContent>
            </Tooltip>
          )}
        </span>
      </td>
    </tr>
  );
}

export function TherapistSessionsTable({
  sessions,
  isLoading,
  therapistUserId,
}: {
  sessions: TherapySession[];
  isLoading: boolean;
  therapistUserId: string;
}) {
  const [filter, setFilter] = useState<Filter>("all");
  const [page, setPage] = useState(1);
  const [newestFirst, setNewestFirst] = useState(true);

  const counts = useMemo(() => {
    const base: Record<Filter, number> = {
      all: sessions.length,
      scheduled: 0,
      completed: 0,
      cancelled: 0,
      no_show: 0,
    };
    for (const s of sessions) base[s.status]++;
    return base;
  }, [sessions]);

  const visible = useMemo(() => {
    const rows = filter === "all" ? sessions : sessions.filter((s) => s.status === filter);
    return [...rows].sort((a, b) => {
      const diff = new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime();
      return newestFirst ? diff : -diff;
    });
  }, [sessions, filter, newestFirst]);

  const totalPages = Math.max(1, Math.ceil(visible.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const rows = visible.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  return (
    <Panel
      title="Sesije"
      count={sessions.length}
      bodyClassName="p-0"
      action={
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => {
                setFilter(f.value);
                setPage(1);
              }}
              className={cn(
                "rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors",
                filter === f.value
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              {f.label}
              <span className="ml-1 tabular-nums opacity-60">{counts[f.value]}</span>
            </button>
          ))}
        </div>
      }
    >
      {isLoading ? (
        <div className="space-y-px p-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-11 animate-pulse rounded bg-muted/40" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-muted-foreground">
          {filter === "all"
            ? "Ovaj terapeut još nema evidentiranih sesija."
            : "Nema sesija sa ovim statusom."}
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[46rem] border-collapse text-left">
              <thead>
                <tr className="text-[10px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
                  <th scope="col" className="px-3 pb-2 pt-1 font-semibold">
                    <button
                      type="button"
                      onClick={() => setNewestFirst((v) => !v)}
                      className="inline-flex items-center gap-1 uppercase tracking-[0.09em] transition-colors hover:text-foreground"
                    >
                      Datum
                      {newestFirst ? (
                        <ArrowDown className="size-3" />
                      ) : (
                        <ArrowUp className="size-3" />
                      )}
                    </button>
                  </th>
                  <th scope="col" className="px-3 pb-2 pt-1 font-semibold">
                    Klijent
                  </th>
                  <th scope="col" className="px-3 pb-2 pt-1 font-semibold">
                    Usluga
                  </th>
                  <th scope="col" className="px-3 pb-2 pt-1 font-semibold">
                    Status
                  </th>
                  <th scope="col" className="px-3 pb-2 pt-1 font-semibold">
                    Ocjena
                  </th>
                  <th scope="col" className="px-3 pb-2 pt-1 font-semibold">
                    Otkazao
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <SessionRow key={s.id} session={s} therapistUserId={therapistUserId} />
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-border/50 px-3 py-2">
              <p className="text-xs tabular-nums text-muted-foreground">
                {(safePage - 1) * PER_PAGE + 1} do{" "}
                {Math.min(safePage * PER_PAGE, visible.length)} od {visible.length}
              </p>
              <div className="flex gap-1.5">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="rounded-md border px-2.5 py-1 text-xs font-medium transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-40"
                >
                  Prethodna
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="rounded-md border px-2.5 py-1 text-xs font-medium transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-40"
                >
                  Sljedeća
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
